import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatRelative, parseJSON } from "date-fns";
import { Box, Grid, Image, ResponsiveContext, Stack, Text } from "grommet";
import { first, orderBy } from "lodash";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TeamName } from "../teams/TeamName";
import { getMatch, MatchParticipantViewModel, MatchViewModel } from "./MatchesStore";
import { RenderOutcome } from "./OutcomeRenderer";

export const SmallMatchRow = ({ match }: { match: MatchViewModel }) => {
    let [teamOne, teamTwo] = match.participants as MatchParticipantViewModel[];
    let teamOneOutcome = first(orderBy((teamOne && teamOne.outcomes) || [], ["timestamp"], ["desc"]));
    let teamTwoOutcome = first(orderBy((teamTwo && teamTwo.outcomes) || [], ["timestamp"], ["desc"]));

    let navigate = useNavigate();

    const size = React.useContext(ResponsiveContext);
    
    // Determine if there's a winner
    const hasWinner = teamOneOutcome?.outcomeType === "Win" || teamTwoOutcome?.outcomeType === "Win";
    const teamOneWon = teamOneOutcome?.outcomeType === "Win";
    const teamTwoWon = teamTwoOutcome?.outcomeType === "Win";

    const navigationProps = match.id ? { hoverIndicator: "background-back", onClick: () => {} } : {};
    const linkProps = match.id ? { to: `/matches/${match.id}/` } : { to: "" };

    const GridProps =
        size == "small"
            ? {
                  rows: ["auto"],
                  columns: [["50px", "50%"], "100px", ["50px", "50%"]],
              }
            : { rows: ["auto"], columns: ["50px", ["80px", "50%"], "100px", ["80px", "50%"], "50px"] };

    const imageSize = "40px";

    return (
        <Link style={{ textDecoration: "none", color: "unset" }} {...linkProps}>
            <Stack anchor="center" interactiveChild={0}>
                <Box background={"background"} pad="small" {...navigationProps} style={{ minHeight: "64px" }}>
                    {match.participants.length <= 2 && (
                        <Grid {...GridProps} fill>
                            {teamOne && (
                                <Box align="start" justify="center" fill>
                                    <img src={teamOne.team.logoUri} alt="" style={{ maxHeight: imageSize }} />
                                    {size == "small" && <TeamName team={teamOne.team} size={"small"} color=""></TeamName>}
                                </Box>
                            )}
                            {size != "small" && teamOne && (
                                <Box align="start" justify="center">
                                    <TeamName team={teamOne.team} size={"small"} color=""></TeamName>
                                </Box>
                            )}
                            <div></div>
                            {size != "small" && teamTwo && (
                                <Box align="end" justify="center">
                                    <TeamName team={teamTwo.team} size={"small"} color=""></TeamName>
                                </Box>
                            )}
                            {teamTwo && (
                                <Box align="end" justify="center" fill>
                                    <img src={teamTwo.team.logoUri} alt="" style={{ maxHeight: imageSize }} />
                                    {size == "small" && <TeamName team={teamTwo.team} size={"small"} color=""></TeamName>}
                                </Box>
                            )}
                        </Grid>
                    )}
                    {match.participants.length > 2 && (
                        <Box direction="row" justify="evenly">
                            {match.participants.map((participant) => (
                                <Box direction="row" key={participant.team.id} align="center" gap="small">
                                    <Box align="center">
                                        <Image width={imageSize} fit="cover" src={participant.team.logoUri} />
                                        <TeamName team={participant.team} size={"medium"} color=""></TeamName>
                                    </Box>
                                    <RenderOutcome outcome={first(orderBy(participant.outcomes, ["timestamp"], ["desc"]))} size={size}></RenderOutcome>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                {/* Winner Banner */}
                {hasWinner && (
                    <Box 
                        pad="xsmall" 
                        fill="horizontal" 
                        background={{
                            color: "#00C781",
                            opacity: "strong"
                        }}
                        style={{ 
                            boxShadow: "0 2px 10px rgba(0, 199, 129, 0.6)",
                            borderLeft: "3px solid #00FF9D",
                            borderRight: "3px solid #00FF9D"
                        }}
                    >
                        <Box direction="row" align="center" justify="center" gap="xsmall">
                            <FontAwesomeIcon icon={["fad", "trophy-star"]} style={{ color: "#FFD700", fontSize: "14px" }} />
                            <Text 
                                size="small" 
                                weight="bold" 
                                color="white" 
                                style={{ 
                                    textTransform: "uppercase", 
                                    letterSpacing: "1px",
                                    textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                                }}
                            >
                                Winner: {teamOneWon ? teamOne?.team?.name : teamTwo?.team?.name}
                            </Text>
                            <FontAwesomeIcon icon={["fad", "trophy-star"]} style={{ color: "#FFD700", fontSize: "14px" }} />
                        </Box>
                    </Box>
                )}

                {match.outcomes.length == 0 && (
                    <Box align="center" justify="center" fill="vertical">
                        <Box pad={"small"}>
                            <Text color="background-front" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                VS
                            </Text>
                        </Box>
                    </Box>
                )}
                {match.outcomes.length > 0 && (
                    <Box direction="row" gap="small" align="center" justify="center">
                        <Box align="center" gap="xsmall">
                            {teamOneWon && (
                                <Box 
                                    background="#00C781" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    style={{ boxShadow: "0 0 10px rgba(0, 199, 129, 0.5)" }}
                                >
                                    <Text size="xsmall" weight="bold" color="white" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Winner
                                    </Text>
                                </Box>
                            )}
                            {teamOneOutcome?.outcomeType === "Loss" && (
                                <Box 
                                    background="rgba(255, 170, 21, 0.3)" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    border={{ color: "#FFAA15", size: "1px" }}
                                >
                                    <Text size="xsmall" weight="bold" color="#FFAA15" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Loss
                                    </Text>
                                </Box>
                            )}
                            <RenderOutcome outcome={teamOneOutcome} size={"small"}></RenderOutcome>
                        </Box>
                        <FontAwesomeIcon icon={["fad", "hyphen"]}></FontAwesomeIcon>
                        <Box align="center" gap="xsmall">
                            {teamTwoWon && (
                                <Box 
                                    background="#00C781" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    style={{ boxShadow: "0 0 10px rgba(0, 199, 129, 0.5)" }}
                                >
                                    <Text size="xsmall" weight="bold" color="white" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Winner
                                    </Text>
                                </Box>
                            )}
                            {teamTwoOutcome?.outcomeType === "Loss" && (
                                <Box 
                                    background="rgba(255, 170, 21, 0.3)" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    border={{ color: "#FFAA15", size: "1px" }}
                                >
                                    <Text size="xsmall" weight="bold" color="#FFAA15" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Loss
                                    </Text>
                                </Box>
                            )}
                            <RenderOutcome outcome={teamTwoOutcome} size={"small"}></RenderOutcome>
                        </Box>
                    </Box>
                )}

                <Box pad={{ bottom: "45px" }} width="400px" align="center">
                    {match.tournamentID && (
                        <Text size="xsmall" style={{ textTransform: "uppercase" }}>
                            {match.tournament.name}
                        </Text>
                    )}
                    {match.seasonID && (
                        <Text size="xsmall" style={{ textTransform: "uppercase" }}>
                            {match.league.name} - {match.season.name}
                        </Text>
                    )}
                </Box>
                {match.startDate && (
                    <Box pad={{ top: "45px" }}>
                        <Text size="small" color={!!match.splashUri ? "white" : "background-front"}>
                            {formatRelative(parseJSON(match.startDate), new Date())}
                        </Text>
                    </Box>
                )}
            </Stack>
        </Link>
    );
};
